<template>
  <view class="bottom-navbar">
    <view
      class="nav-item"
      v-for="(item, index) in navItems"
      :key="index"
      :class="{ 'active': activeIndex === index }"
      @click="handleNavClick(index)"
    >
      <view class="nav-icon-wrapper">
        <image 
          class="nav-icon" 
          :src="getIconSrc(index)" 
          :class="{ 'icon-active': activeIndex === index }"
        />
      </view>
      <text class="nav-text" :class="{ 'text-active': activeIndex === index }">{{ item.text }}</text>
    </view>
  </view>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { onLoad, onShow } from '@dcloudio/uni-app';

interface NavItem {
  icon: string;
  activeIcon: string;
  text: string;
  path: string;
}

// 使用 Vite 的资源处理功能导入图标
const storeIcon = new URL('/src/static/tabbar/store.png', import.meta.url).href;
const storeActiveIcon = new URL('/src/static/tabbar/store-active.png', import.meta.url).href;
const searchIcon = new URL('/src/static/tabbar/search.png', import.meta.url).href;
const searchActiveIcon = new URL('/src/static/tabbar/search-active.png', import.meta.url).href;
const profileIcon = new URL('/src/static/tabbar/profile.png', import.meta.url).href;
const profileActiveIcon = new URL('/src/static/tabbar/profile-active.png', import.meta.url).href;

const navItems: NavItem[] = [
  {
    icon: storeIcon,
    activeIcon: storeActiveIcon,
    text: '门店',
    path: '/pages/index/index'
  },
  {
    icon: searchIcon,
    activeIcon: searchActiveIcon,
    text: '查询',
    path: '/pages/search/search'
  },
  {
    icon: profileIcon,
    activeIcon: profileActiveIcon,
    text: '我的',
    path: '/pages/profile/profile'
  }
];

const activeIndex = ref<number>(0);

const getIconSrc = (index: number): string => {
  if (activeIndex.value === index) {
    return navItems[index].activeIcon;
  }
  return navItems[index].icon;
};

const updateActiveIndex = (): void => {
  const pages = getCurrentPages();
  if (pages.length > 0) {
    const currentPage = pages[pages.length - 1];
    const currentRoute = `/${currentPage.route}`;
    const idx = navItems.findIndex(item => item.path === currentRoute);
    if (idx !== -1) {
      activeIndex.value = idx;
    }
  }
};

// 监听页面显示，确保每次页面显示时都更新激活状态
onShow(() => {
  updateActiveIndex();
});

// 页面加载时更新激活状态
onLoad(() => {
  updateActiveIndex();
});

const handleNavClick = (index: number): void => {
  // 如果点击的是当前已激活的项，不做任何操作
  if (activeIndex.value === index) return;
  
  activeIndex.value = index;
  uni.switchTab({
    url: navItems[index].path,
    success: () => {
      // 导航成功后再次更新激活状态，确保状态同步
      setTimeout(updateActiveIndex, 100);
    }
  });
};
</script>

<style lang="scss" scoped>
@import '@/styles/vars.scss';

.bottom-navbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 120rpx;
  background-color: $bg-primary;
  display: flex;
  justify-content: space-around;
  align-items: center;
  border-top: 1rpx solid $border-color;
  z-index: 999;
  padding-bottom: env(safe-area-inset-bottom);
  box-shadow: 0 -2rpx 10rpx rgba(0, 0, 0, 0.05);
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex: 1;
  transition: all $transition-normal $ease-in-out;
  position: relative;
  padding: $spacing-2 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%) scaleX(0);
    width: 40rpx;
    height: 6rpx;
    background-color: $primary-color;
    border-radius: 0 0 $border-radius-full $border-radius-full;
    transition: transform $transition-normal $ease-out;
  }

  &.active {
    &::before {
      transform: translateX(-50%) scaleX(1);
    }
  }

  &:active {
    transform: scale(0.95);
  }
}

.nav-icon-wrapper {
  margin-bottom: $spacing-1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64rpx;
  height: 64rpx;
  border-radius: 50%;
  transition: all $transition-normal $ease-out;
  position: relative;
}

.nav-item.active .nav-icon-wrapper {
  background-color: rgba($primary-color, 0.15);
  box-shadow: 0 0 10rpx rgba($primary-color, 0.2);
}

.nav-icon {
  width: 48rpx;
  height: 48rpx;
  transition: all $transition-normal $ease-out;
  filter: grayscale(100%);
  opacity: 0.7;
  will-change: transform, opacity, filter;
}

.nav-item.active .nav-icon {
  filter: grayscale(0%);
  opacity: 1;
  transform: scale(1.1);
}

.nav-item:active .nav-icon-wrapper {
  transform: scale(0.9);
}

/* 优化图标切换动画 */
.nav-icon-wrapper {
  overflow: hidden;
}

.nav-icon {
  transition: all $transition-normal $ease-out;
}

.nav-text {
  font-size: $font-size-caption;
  color: $text-secondary;
  transition: all $transition-normal $ease-out;
  line-height: 1;
  font-weight: $font-weight-regular;
}

.nav-text.text-active {
  color: $primary-color;
  font-weight: $font-weight-medium;
}

/* 添加脉冲动画效果 */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba($primary-color, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10rpx rgba($primary-color, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba($primary-color, 0);
  }
}

.nav-item.active .nav-icon-wrapper {
  animation: pulse 2s infinite;
}
</style>